import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Popover, PopoverModule } from 'primeng/popover';
import { Subscription } from 'rxjs';

import { RelativeTimePipe } from '../../../shared/pipes/relative-time.pipe';
import { SoundNotificationService } from '../../../core/notifications/sound-notification.service';
import { NotificationsHubService } from '../../../core/realtime/notifications-hub.service';
import { NotificationsApiService } from '../notifications-api.service';
import { NotificationItem } from '../notifications.models';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

/**
 * Phase 5's Notifications: SignalR notifications drive this bell's badge/list in
 * real time; the same event also fires a PrimeNG toast and, if the user has granted
 * permission, a native browser Notification -- three of the MVP's four Notifications
 * checklist items are all client-side reactions to one NotificationCreated push. Email is
 * the only channel that needs server-side sending (NotificationDispatcherJob).
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule, ButtonModule, OverlayBadgeModule, PopoverModule, RelativeTimePipe, TranslatePipe],
  templateUrl: './notification-bell.component.html',
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private readonly api = inject(NotificationsApiService);
  private readonly hub = inject(NotificationsHubService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly soundNotifications = inject(SoundNotificationService);
  private readonly subscriptions = new Subscription();

  @ViewChild('panel') panel?: Popover;

  readonly notifications = signal<NotificationItem[]>([]);
  readonly unreadCount = signal(0);

  ngOnInit(): void {
    void this.hub.start();
    this.load();
    this.requestBrowserPermission();

    this.subscriptions.add(
      this.hub.notificationCreated$.subscribe((event) => {
        const item: NotificationItem = {
          notificationId: event.notificationId,
          type: event.type,
          title: event.title,
          body: event.body,
          deepLinkUrl: event.deepLinkUrl,
          isRead: false,
          createdAtUtc: new Date().toISOString(),
        };

        this.notifications.update((current) => [item, ...current].slice(0, 25));
        this.unreadCount.update((count) => count + 1);

        this.messageService.add({ severity: 'info', summary: event.title, detail: event.body, life: 6000 });
        this.showBrowserNotification(event.title, event.body);
        this.soundNotifications.play(event.type);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  togglePanel(event: Event): void {
    this.panel?.toggle(event);
  }

  open(notification: NotificationItem): void {
    if (!notification.isRead) {
      this.markRead(notification);
    }

    this.panel?.hide();

    if (notification.deepLinkUrl) {
      void this.router.navigateByUrl(notification.deepLinkUrl);
    }
  }

  markRead(notification: NotificationItem): void {
    if (notification.isRead) {
      return;
    }

    this.api.markRead(notification.notificationId).subscribe(() => {
      notification.isRead = true;
      this.notifications.update((current) => [...current]);
      this.unreadCount.update((count) => Math.max(0, count - 1));
    });
  }

  markAllRead(): void {
    this.api.markAllRead().subscribe(() => {
      this.notifications.update((current) => current.map((n) => ({ ...n, isRead: true })));
      this.unreadCount.set(0);
    });
  }

  private load(): void {
    this.api.getNotifications().subscribe((page) => {
      this.notifications.set(page.items);
      this.unreadCount.set(page.unreadCount);
    });
  }

  private requestBrowserPermission(): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') {
      return;
    }

    void Notification.requestPermission();
  }

  private showBrowserNotification(title: string, body: string): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      return;
    }

    new Notification(title, { body });
  }
}
